import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, ComposedChart,
  Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { fetchPromisesData } from "../../../services/dashboardModule/dashboardServices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/promisesConstants.js";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
const PURPLE = "#7F77DD";

// ── Helpers ────────────────────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)    return `$${(num / 1000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

function fmtDate(val) {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE }) {
  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 14px", minWidth: 0, flex: 1,
    }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Promises = () => {
  const today        = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate,     setStartDate]     = useState(firstOfMonth);
  const [endDate,       setEndDate]       = useState(today);
  const [promisesType,  setPromisesType]  = useState("Texts");
  const [data,          setData]          = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState("");

  const load = useCallback(async (start, end, type) => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchPromisesData({
        startDate:    formatDateTime(start),
        endDate:      formatDateTime(end),
        promisesType: type,
      });
      setData(result || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load promises data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(firstOfMonth, today, "Texts");
  }, []);

  // ── KPIs ───────────────────────────────────────────────────────
  // Kept % is Kept ÷ PTP for both counts and $ — the same basis used
  // across the other pages.
  const kpis = useMemo(() => {
    const total       = data.length;
    const kept        = data.filter(r => safeNum(r.PromiseKept)    === 1).length;
    const broken      = data.filter(r => safeNum(r.PromiseBroken)  === 1).length;
    const partial     = data.filter(r => safeNum(r.PartialPayment) === 1).length;
    const totalAmt    = data.reduce((a,b) => a + safeNum(b.PromiseAmountDollar),  0);
    const keptAmt     = data.reduce((a,b) => a + safeNum(b.PromiseKeptAmount),    0);
    const brokenAmt   = data.reduce((a,b) => a + safeNum(b.PromiseBrokenAmount),  0);
    const keptPct     = total    > 0 ? kept    / total    * 100 : 0;
    const brokenPct   = total    > 0 ? broken  / total    * 100 : 0;
    const keptAmtPct  = totalAmt > 0 ? keptAmt / totalAmt * 100 : 0;
    return { total, kept, broken, partial, totalAmt, keptAmt, brokenAmt, keptPct, brokenPct, keptAmtPct };
  }, [data]);

  // ── Chart data — grouped by ActivityDate ──────────────────────
  const chartData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const key = fmtDate(r.ActivityDate);
      if (!map[key]) map[key] = {
        date: key,
        total: 0, kept: 0, broken: 0,
        totalAmt: 0, keptAmt: 0, brokenAmt: 0,
      };
      map[key].total    += 1;
      map[key].kept     += safeNum(r.PromiseKept);
      map[key].broken   += safeNum(r.PromiseBroken);
      map[key].totalAmt += safeNum(r.PromiseAmountDollar);
      map[key].keptAmt  += safeNum(r.PromiseKeptAmount);
      map[key].brokenAmt += safeNum(r.PromiseBrokenAmount);
    });
    return Object.values(map).sort((a,b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // ── Table columns ──────────────────────────────────────────────
  const COLS    = "90px 160px 90px 110px 110px 110px 90px 110px";
  const HEADERS = [
    "Made Date", "Customer", "PTP Amt", "PTP Date",
    "Last Pay Amt", "Last Pay Date", "Partial", "Kept"
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, promisesType)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setPromisesType(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  promisesType === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  promisesType === ch ? BLUE : "transparent",
              color:       promisesType === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard label="PTP"             value={kpis.total.toLocaleString()}               color={BLUE}  />
            <KpiCard label="Kept"            value={kpis.kept.toLocaleString()}                color={GREEN} />
            <KpiCard label="Broken"          value={kpis.broken.toLocaleString()}              color={PINK}  />
            <KpiCard label="Partial payment" value={kpis.partial.toLocaleString()}             color={AMBER} />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiCard label="PTP amount"    value={fmtCurrency(kpis.totalAmt)}                  color={BLUE}  />
            <KpiCard label="Kept amount"   value={fmtCurrency(kpis.keptAmt)}                   color={GREEN} />
            <KpiCard label="Broken amount" value={fmtCurrency(kpis.brokenAmt)}                 color={PINK}  />
            <KpiCard label="Kept %"        value={`${kpis.keptPct.toFixed(2)}%`}               color={GREEN} />
            <KpiCard label="Kept $ %"      value={`${kpis.keptAmtPct.toFixed(2)}%`}            color={GREEN} />
            <KpiCard label="Broken %"      value={`${kpis.brokenPct.toFixed(2)}%`}             color={PINK}  />
          </Box>

          {/* ── PTP AMOUNT OVER TIME ──────────────────────────── */}
          <SectionTitle>PTP amount over time</SectionTitle>

          <ChartCard height={220}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} tickFormatter={v => fmtCurrency(v)} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) => [fmtCurrency(v), n]}
                />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="keptAmt"   name="Kept amount"   fill={GREEN} radius={[3,3,0,0]} />
                <Bar dataKey="brokenAmt" name="Broken amount" fill={PINK}  radius={[3,3,0,0]} />
                <Line type="monotone" dataKey="total" name="PTP"
                  stroke={AMBER} dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── KEPT VS BROKEN COUNT ──────────────────────────── */}
          <SectionTitle>Kept vs broken</SectionTitle>

          <ChartCard height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="kept"   name="Kept"   fill={GREEN}  radius={[3,3,0,0]} />
                <Bar dataKey="broken" name="Broken" fill={PINK}   radius={[3,3,0,0]} />
                <Bar dataKey="total"  name="PTP"    fill={PURPLE} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={h} sx={{ textAlign: i === 0 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {data.slice(0, 100).map((row, i) => {
              const kept    = safeNum(row.PromiseKept)    === 1;
              const partial = safeNum(row.PartialPayment) === 1;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                }}>
                  <Box>{fmtDate(row.ActivityDate)}</Box>
                  <Box sx={{ textAlign: "right", fontSize: 11, color: "text.secondary",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {row.Customer ?? "—"}
                  </Box>
                  <Box sx={{ textAlign: "right", color: BLUE, fontWeight: 500 }}>
                    {fmtCurrency(row.PromiseAmountDollar)}
                  </Box>
                  <Box sx={{ textAlign: "right" }}>{fmtDate(row.PromiseDate)}</Box>
                  <Box sx={{ textAlign: "right" }}>
                    {fmtCurrency(row.LastPaymentAmountDollar)}
                  </Box>
                  <Box sx={{ textAlign: "right" }}>{fmtDate(row.LastPaymentDate)}</Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Box component="span" sx={{
                      px: "6px", py: "1px", borderRadius: "10px", fontSize: 11,
                      background: partial ? "rgba(186,117,23,0.12)" : "transparent",
                      color: partial ? AMBER : "text.secondary",
                      fontWeight: partial ? 500 : 400,
                    }}>
                      {partial ? "Yes" : "No"}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Box component="span" sx={{
                      px: "6px", py: "1px", borderRadius: "10px", fontSize: 11,
                      background: kept ? "rgba(29,158,117,0.12)" : "rgba(212,83,126,0.1)",
                      color: kept ? GREEN : PINK,
                      fontWeight: 500,
                    }}>
                      {kept ? "Kept" : "Broken"}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Promises;